import {
    EzBadParamException,
    EzBadStateException,
    EzStaticClassException
} from '/ezlibrary/exceptions/EzExceptions.js';

/**
 * @public
 * @class {EzStaticClass}
 * @description
 * A base class that prevents creation of new instances for classes that extend EzStaticClass.
 * ---------------------------------------------------------------------------
 * Import with:
 *     import { EzStaticClass } from '/ezlibrary/EzStaticClass.js';
 * ---------------------------------------------------------------------
 * Import with other ezClocker JS classes:
 *      import {
 *         EzClass,
 *         ... other supported classes ...
 *     } from '/ezlibrary/ezclocker-classes/EzClasses.js';
 * ---------------------------------------------------------------------
 */
export class EzStaticClass {
    /**
     * @protected
     * @constructor
     * >> DO NOT CREATE NEW INSTANCES <<
     * Never create a new instance of a class that extends EzStaticClass
     * Creating a new instance will throw EzStaticClassException
     * Only use static properties, fields, and methods available from the class.
     */
    constructor() {
        throw new EzStaticClassException(this);
    }

    /**
     * @public @static @readonly @property
     * Returns true to indicate the class is a EzStaticClass
     * @returns {boolean}
     */
    static get isEzStaticClass() {
        return true;
    }

    /**
     * @static
     * @protected @function
     * Adds a static function named via the provided propertyName with the functionRef implementation.
     * @param {Object} clazzReference
     * The class to which the static property will be added.
     * Required non-null
     * @param {String} functionName
     * Required valid, non-empty string that will be the name of the static function added to the provided clazzReference.
     * @param {Function} functionRef
     * Required non-null function reference that will be added as a static function to the provided clazzReference with the provided functionName.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addStaticFunctionToClass(clazzReference, functionName, functionRef) {
        if (undefined === clazzReference || null === clazzReference) {
            throw new EzBadParamException(
                { clazzReference },
                this,
                this.addStaticFunctionToClass);
        }
        if (undefined === functionName || null === functionName ||
            'string' !== typeof functionName || 0 === functionName.trim().length) {
            throw new EzBadParamException(
                { functionName },
                this,
                this.addStaticFunctionToClass);
        }
        if (undefined === functionRef || null === functionRef) {
            throw new EzBadParamException(
                { functionRef },
                this,
                this.addStaticFunctionToClass);
        }
        if ('function' !== typeof clazzReference) {
            throw new EzBadStateException(
                `Expected clazzReference to be a class/function reference.`,
                `However, typeof clazzReference is ${typeof clazzReference}`,
                this,
                this.addStaticFunctionToClass);
        }
        if ('function' !== typeof functionRef) {
            throw new EzBadStateException(
                `Expected functionRef to be a function reference.`,
                `However, typeof functionRef is ${typeof functionRef}`,
                this,
                this.addStaticFunctionToClass);
        }
        /*if ('function' === typeof clazzReference?.[functionName]) {
            globalThis?.console?.warn(
                `Redefining existing function ${functionName} on class ${clazzReference?.name}`);
        }*/

        const reservedFunctionNames = new Set(['__proto__', 'constructor', 'prototype']);

        if (!clazzReference?.functionName && !reservedFunctionNames.has(functionName)) {
            clazzReference[functionName] = functionRef;
        }

        return clazzReference;
    }

    /**
     * @static
     * @protected @function
     * Adds a static property to the provided clazz with the provided property name and property value.
     * @param {Object} clazzReference - The class to which the static property will be added.
     * Required non-null
     * @param {undefined|null|String} propertyName - The name of the static property.
     * Required valid, non-empty string
     * @param {*} propertyValue - The value of the static property.
     * @returns {object}
     * Returns the object that was passed to the function, with the specified property added or modified.
     */
    static addStaticPropertyToClass(clazzReference, propertyName, propertyValue) {
        if (undefined === clazzReference || null === clazzReference) {
            throw new EzBadParamException(
                { clazzReference },
                this,
                this.addStaticPropertyToClass);
        }
        if (undefined === propertyName || null === propertyName ||
            'string' !== typeof propertyName || 0 === propertyName.trim().length) {
            throw new EzBadParamException(
                { propertyName },
                this,
                this.addStaticPropertyToClass);
        }
        if ('function' !== typeof clazzReference) {
            throw new EzBadStateException(
                `Expected clazzReference to be a class/function reference.`,
                `However, typeof clazzReference is ${typeof clazzReference}`,
                this,
                this.addStaticPropertyToClass);
        }

        if (!clazzReference?.propertyName) {
            try {
            return Object.defineProperty(
                clazzReference,
                propertyName,
                {
                    get: function() {
                        return propertyValue;
                    }
                });
            } catch (err) {
                globalThis?.console?.error(`Failed to add static property ${propertyName} to class ${clazzReference?.name}`);

                throw err;
            }
        }
    }
}
